/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable no-param-reassign */
import SettingsIcon from "@mui/icons-material/Settings";
import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import * as Cesium from "cesium";
import React, { useEffect, useMemo, useState } from "react";
import { Color } from "react-color";
import { useTranslation } from "react-i18next";
import {
  CesiumPointSettings,
  ColorSettings,
} from "../../functions/colorAdjustments";
import { updateTilesetStyle } from "../../functions/updateTilesetStyle";
import { Custom3DTileset } from "../../types/CesiumMapItems";
import { ColorAdjustmentDialog } from "./ColorAdjustmentDialog";
import SingleColorPicker from "./SingleColorPicker";

export type PointColoringType =
  | "RGB"
  | "elevation"
  | "static"
  | "intensity"
  | "default";

const ColoringTypes: Record<PointColoringType, { translationKey: string }> = {
  RGB: { translationKey: "RGB" },
  elevation: { translationKey: "elevation" },
  intensity: { translationKey: "intensity" },
  static: { translationKey: "static" },
  default: { translationKey: "default" },
};

const ColoringKeys = Object.keys(ColoringTypes) as Array<PointColoringType>;

interface Props {
  tilesets: Custom3DTileset[];
  onElevationSettingsChange: (val: string) => void;
  onIntensitySettingsChange: (val: string) => void;
  onColorSettingsChange: (val: string) => void;
  onStaticColorChange: (val: string) => void;
  onColoringTypeChange: (val: string) => void;
}

const PointColorAdjustment = ({
  tilesets,
  onElevationSettingsChange,
  onIntensitySettingsChange,
  onColoringTypeChange,
  onStaticColorChange,
  onColorSettingsChange,
}: Props): JSX.Element => {
  const { t } = useTranslation("cesium");
  const [useIndividualFileValuesTemp] = useState(tilesets[0].individualValues);
  const [, setUseIndividualFileValues] = useState(tilesets[0].individualValues);
  const [staticColor, setStaticColor] = useState<Color>(
    tilesets[0].staticColor,
  );
  const [staticColorTemp, setStaticColorTemp] = useState<Color>(
    tilesets[0].staticColor,
  );
  const [coloringType, setColoringType] = useState<PointColoringType>(
    tilesets[0].colorType,
  );
  const [coloringTypeTemp, setColoringTypeTemp] = useState<PointColoringType>(
    tilesets[0].colorType,
  );
  const [updated, setUpdated] = useState(false);
  const [colorAdjustmentDialogOpen, setColorAdjustmentDialogOpen] =
    React.useState(false);
  const [colorSettings, setColorSettings] =
    React.useState<CesiumPointSettings>();

  // Parse values from the tilesets to state
  useEffect(() => {
    setStaticColor(tilesets[0].staticColor);
    setColoringType(tilesets[0].colorType ?? "default");
    setUseIndividualFileValues(tilesets[0].individualValues);
  }, [updated, tilesets]);

  const getTilesetMinMaxValues = React.useCallback(
    (tileset: Cesium.Cesium3DTileset) => {
      const minZ = useIndividualFileValuesTemp
        ? (tileset as Custom3DTileset).minZ
        : tilesets.reduce((prev, cur) => (prev.minZ < cur.minZ ? prev : cur))
            .minZ;

      const maxZ = useIndividualFileValuesTemp
        ? (tileset as Custom3DTileset).maxZ
        : tilesets.reduce((prev, cur) => (prev.maxZ > cur.maxZ ? prev : cur))
            .maxZ;

      const minIntensity = useIndividualFileValuesTemp
        ? (tileset as Custom3DTileset).minIntensity
        : tilesets.reduce((prev, cur) =>
            prev.minIntensity < cur.minIntensity ? prev : cur,
          ).minIntensity;

      const maxIntensity = useIndividualFileValuesTemp
        ? (tileset as Custom3DTileset).maxIntensity
        : tilesets.reduce((prev, cur) =>
            prev.maxIntensity > cur.maxIntensity ? prev : cur,
          ).maxIntensity;

      return {
        minZ,
        maxZ,
        minIntensity,
        maxIntensity,
      };
    },
    [tilesets, useIndividualFileValuesTemp],
  );

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/require-await
    async function updateTileset() {
      const useColorSettings =
        colorSettings &&
        colorSettings.color.conditions.length > 0 &&
        Object.keys(colorSettings.defines).length > 0;

      tilesets.forEach(async (tileset) => {
        let val: string | undefined | ColorSettings;
        tileset.colorType = coloringTypeTemp;
        tileset.staticColor = staticColorTemp.toString();
        tileset.individualValues = useIndividualFileValuesTemp;
        switch (coloringTypeTemp) {
          case "static":
            val = staticColorTemp
              ? `color('${staticColorTemp?.toString()}')`
              : undefined;
            break;

          case "elevation": {
            if (!useColorSettings) break;
            val = colorSettings!.color;
            await updateTilesetStyle({
              tileset,
              type: "defines",
              newValue: colorSettings!.defines,
            });
            break;
          }
          case "RGB":
            val = `\${COLOR}`;
            break;

          case "intensity": {
            await updateTilesetStyle({
              tileset,
              type: "defines",
              newValue: colorSettings!.defines,
            });
            val = colorSettings!.color;

            break;
          }

          case "default":
            val = undefined;
            break;

          default:
            val = undefined;
            break;
        }
        await updateTilesetStyle({
          tileset,
          type: "color",
          newValue: val,
        });
      });
    }
    void updateTileset().then(() => setUpdated((prev) => !prev));
  }, [
    tilesets,
    coloringTypeTemp,
    staticColorTemp,
    useIndividualFileValuesTemp,
    getTilesetMinMaxValues,
    colorSettings,
  ]);

  const handleTypeChange = (event: SelectChangeEvent<PointColoringType>) => {
    if (!tilesets[0]) return;
    setColoringTypeTemp(event.target.value as PointColoringType);
  };

  const ColorTypeSelection = useMemo(() => {
    switch (coloringType) {
      case "static":
        return (
          <SingleColorPicker
            color={staticColor}
            onChange={(c) => setStaticColorTemp(c.hex)}
            disableAlpha
          />
        );
      case "elevation":
      case "intensity":
        return (
          <Grid container sx={{ mt: 2 }}>
            <IconButton onClick={() => setColorAdjustmentDialogOpen(true)}>
              <SettingsIcon />
            </IconButton>
          </Grid>
        );

      default:
        return null;
    }
  }, [coloringType, staticColor]);

  const getMinMaxValForAdjustment = React.useCallback(
    (type: "min" | "max") => {
      const vals = getTilesetMinMaxValues(tilesets[0]);
      if (coloringType === "elevation") {
        return type === "min" ? vals.minZ : vals.maxZ;
      }
      return type === "min" ? vals.minIntensity : vals.maxIntensity;
    },
    [coloringType, getTilesetMinMaxValues, tilesets],
  );

  const handleColorAdjustmentDialogClose = React.useCallback(
    () => setColorAdjustmentDialogOpen(false),
    [],
  );
  const handleColorSettingsChange = React.useCallback(
    (newSettings: CesiumPointSettings) => setColorSettings(newSettings),
    [],
  );

  React.useEffect(() => {
    if (!colorSettings) return;
    onColorSettingsChange(JSON.stringify(colorSettings));
  }, [colorSettings, onColorSettingsChange]);
  React.useEffect(() => {
    if (!staticColor) return;
    onStaticColorChange(JSON.stringify(staticColor));
  }, [staticColor, onStaticColorChange]);
  React.useEffect(() => {
    if (!coloringType) return;
    onColoringTypeChange(JSON.stringify(coloringType));
  }, [coloringType, onColoringTypeChange]);

  const initialSettings = React.useMemo(() => {
    const blendingMode =
      coloringType === "elevation"
        ? tilesets[0]?.elevationSettings?.blendingMode
        : tilesets[0]?.intensitySettings?.blendingMode;
    const valueMode =
      coloringType === "elevation"
        ? tilesets[0]?.elevationSettings?.valueMode
        : tilesets[0]?.intensitySettings?.valueMode;
    const colorsAndSteps =
      coloringType === "elevation"
        ? tilesets[0]?.elevationSettings?.colorsAndSteps
        : tilesets[0]?.intensitySettings?.colorsAndSteps;
    const minValAbs =
      coloringType === "elevation"
        ? tilesets[0]?.elevationSettings?.minZ
        : tilesets[0]?.intensitySettings?.minIntensity;
    const maxValAbs =
      coloringType === "elevation"
        ? tilesets[0]?.elevationSettings?.maxZ
        : tilesets[0]?.intensitySettings?.maxIntensity;
    return {
      blendingMode: blendingMode ?? undefined,
      valueMode: valueMode ?? undefined,
      colorsAndSteps: colorsAndSteps ?? undefined,
      minValAbs: minValAbs ?? undefined,
      maxValAbs: maxValAbs ?? undefined,
    };
  }, [tilesets, coloringType]);

  return (
    <>
      <Grid item xs={ColorTypeSelection ? 9 : 12}>
        <FormControl fullWidth>
          <InputLabel id="coloring-type-selection">
            {t("point-color-type")}
          </InputLabel>
          <Select
            labelId="coloring-type-selection"
            value={coloringType}
            onChange={handleTypeChange}
            fullWidth
          >
            {ColoringKeys.map((k) => {
              const disabled =
                (k === "intensity" &&
                  !tilesets.every((ts) => ts.hasIntensity)) ||
                (k === "RGB" && !tilesets.every((ts) => ts.hasRGB)) ||
                (k === "elevation" &&
                  !tilesets.every((ts) => ts.minZ || ts.maxZ));
              return (
                // <Tooltip
                //   title={disabled ? t("value-not-found").toString() : ""}
                // >
                <MenuItem disabled={disabled} value={k} key={k}>
                  {t(`cesium-color:${ColoringTypes[k].translationKey}`)}
                </MenuItem>
                // </Tooltip>
              );
            })}
          </Select>
          {/* <FormControlLabel
            control={
              <Checkbox
                color="secondary"
                checked={useIndividualFileValues}
                onChange={(_e, v) => setUseIndividualFileValuesTemp(v)}
              />
            }
            label={t("individual-file-values")}
          /> */}
        </FormControl>
      </Grid>
      {ColorTypeSelection && (
        <Grid item xs={3}>
          {ColorTypeSelection}
        </Grid>
      )}
      {coloringType === "elevation" || coloringType === "intensity" ? (
        <ColorAdjustmentDialog
          open={colorAdjustmentDialogOpen}
          onClose={handleColorAdjustmentDialogClose}
          initialColoringBlendingMode={initialSettings.blendingMode}
          initialColoringValueMode={initialSettings.valueMode}
          initialColorsAndSteps={initialSettings.colorsAndSteps}
          onChange={handleColorSettingsChange}
          onElevationStringChange={onElevationSettingsChange}
          onIntensityStringChange={onIntensitySettingsChange}
          initialMinValAbs={
            initialSettings.minValAbs ?? getMinMaxValForAdjustment("min")
          }
          initialMaxValAbs={
            initialSettings.maxValAbs ?? getMinMaxValForAdjustment("max")
          }
          tilesetMinValAbs={getMinMaxValForAdjustment("min")}
          tilesetMaxValAbs={getMinMaxValForAdjustment("max")}
          colorCondition={coloringType}
        />
      ) : null}
    </>
  );
};

export default PointColorAdjustment;
